/**
 * Utilidades para trackear uso de herramientas por vendedor.
 *
 * Un dispositivo se "reclama" para un vendedor cuando:
 *   - Ingresa con su NIP al portal `/vendedor` (setDeviceSeller en login)
 *   - Abre el catálogo con su link personalizado `/catalogo?v=<slug>`
 *
 * A partir de ese momento se registran eventos (portal_view, catalog_view,
 * offers_view, shipments_toggle, history_toggle) con su seller_id. El admin
 * puede consultar el uso en `/vendedores` → pestaña "Uso de herramientas".
 */
import { useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEVICE_SELLER_KEY = 'device_seller_id';

export function getDeviceSellerId() {
  try { return localStorage.getItem(DEVICE_SELLER_KEY) || null; }
  catch { return null; }
}

export function setDeviceSeller(sellerId) {
  try {
    if (sellerId) localStorage.setItem(DEVICE_SELLER_KEY, sellerId);
    else localStorage.removeItem(DEVICE_SELLER_KEY);
  } catch { /* ignore */ }
}

/**
 * Envía un evento de actividad. Si `durationSec` viene y > 0, se acumula
 * como tiempo (no cuenta como nueva vista).
 */
export function trackSellerActivity(event, durationSec = null) {
  const sid = getDeviceSellerId();
  if (!sid) return;
  const body = { seller_id: sid, event };
  if (durationSec != null && durationSec > 0) body.duration_sec = durationSec;
  try {
    fetch(`${API}/public/seller-activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

/**
 * Hook: dispara un evento al montar la página y otro con la duración al
 * desmontar (para calcular tiempo promedio). Solo cuenta duraciones entre
 * 3s y 60min para descartar rebotes y sesiones olvidadas.
 */
export function useTrackPage(event) {
  useEffect(() => {
    const sid = getDeviceSellerId();
    if (!sid) return undefined;
    trackSellerActivity(event);
    const t0 = Date.now();
    // Enviar duración también al cerrar/refrescar la pestaña
    const flush = () => {
      const secs = Math.round((Date.now() - t0) / 1000);
      if (secs >= 3 && secs <= 3600) trackSellerActivity(event, secs);
    };
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [event]);
}
